"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import React from "react";
import { useTranslations } from "next-intl";

import { useBanks } from "@/contexts/BanksContext";
import { getOptimizedImageUrl } from "@/utils/utils";

const DynamicSlider = dynamic(() => import("@/components/common/slider/Slider"), {
  ssr: false,
  loading: () => (
    <div className="grid w-full grid-cols-3 items-center gap-2 sm:w-auto lg:grid-cols-4">
      <Skeleton count={3} />
    </div>
  ),
});

interface PartneredWithBanksCardProps {
  isArabic?: boolean;
}

const PartneredWithBanksCard: React.FC<PartneredWithBanksCardProps> = ({ isArabic = false }) => {
  const { banks } = useBanks();
  const t = useTranslations("BankValuation");

  const isLoading = banks.length === 0;

  const displayBanks = React.useMemo(() => {
    if (banks.length === 0) return [];
    return [...banks, ...banks];
  }, [banks]);

  return (
    <div
      className={`mb-4 md:absolute md:top-8 lg:top-16 xl:block xl:flex-shrink-0 ${
        isArabic ? "md:left-0 xl:mr-8" : "md:right-0 xl:ml-8"
      }`}
    >
      <div className="relative w-fit xl:h-80 xl:w-96">
        <div
          className={`rounded-2xl bg-transparent py-3 md:bg-grayishBlue md:px-5 md:backdrop-blur xl:absolute xl:top-8 ${
            isArabic ? "xl:left-0" : "xl:right-0"
          }`}
        >
          <span className="hidden font-urbanist text-white md:block">{t("hero.partneredWith")}</span>

          <div className="mt-2 max-w-64 overflow-hidden lg:max-w-80">
            {isLoading ? (
              <div className="grid grid-cols-3 gap-2 lg:grid-cols-4">
                <Skeleton count={3} />
              </div>
            ) : (
              <DynamicSlider
                navigationEnabled={false}
                paginationEnabled={false}
                slidesPerView={3}
                spaceBetween={8}
                breakpoints={{
                  1024: {
                    slidesPerView: 3.5,
                    spaceBetween: 8,
                  },
                }}
                navigation={{
                  nextEl: ".custom-swiper-button-next-banks",
                  prevEl: ".custom-swiper-button-prev-banks",
                }}
                loop={true}
                autoplay={{
                  delay: 1000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                className="grid grid-cols-3 lg:grid-cols-4"
              >
                {displayBanks.map((bank, index) => (
                  <div
                    key={`${index}-${bank.slug}`}
                    aria-label={bank.name}
                    className="relative h-12 w-20 flex-shrink-0 rounded-lg bg-white p-2"
                  >
                    <Image
                      src={getOptimizedImageUrl(bank.imageUrl)}
                      alt={bank.name}
                      fill
                      sizes="96px"
                      className="rounded-lg object-fill"
                      unoptimized
                    />
                  </div>
                ))}
              </DynamicSlider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Skeleton: React.FC<{ count: number }> = ({ count = 4 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="h-12 w-16 animate-pulse rounded-lg bg-gray-200 p-2"></div>
      ))}
    </>
  );
};

export default PartneredWithBanksCard;
